import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  TeamOutlined,
  RightOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { useTerminology } from '../hooks/useTerminology';
import './Dashboard.css';

interface Assessment {
  assessmentId: string;
  assessmentName: string;
  assessmentType: 'CMA' | 'CMDA';
  status: 'draft' | 'submitted';
  createdAt: string;
  updatedAt: string;
  overallScore?: 'Adequate' | 'Inadequate';
}

interface Entitlement {
  maxUsers: number;
  currentUsers: number;
  canCreateUser: boolean;
  status: string;
}

const Dashboard: React.FC = () => {
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isAdmin, isAssessor } = useAuth();
  const t = useTerminology();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const { user } = useAuth();
  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user?.tenantId, user?.username]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [assessmentsData, entitlementData] = await Promise.all([
        apiService.getAssessments(),
        isAdmin ? apiService.getEntitlement() : Promise.resolve(null)
      ]);

      setAssessments(assessmentsData || []);
      setEntitlement(entitlementData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const recentAssessments = assessments.slice(0, 5);
  const draftCount = assessments.filter(a => a.status === 'draft').length;
  const submittedCount = assessments.filter(a => a.status === 'submitted').length;
  const adequateCount = assessments.filter(a => a.overallScore === 'Adequate').length;

  const usagePct = entitlement
    ? Math.min(Math.round((entitlement.currentUsers / entitlement.maxUsers) * 100), 100)
    : 0;
  const usageWarn = usagePct >= 80;

  if (loading) {
    return (
      <div className="dashboard-root">
        <div className="d-loading"><Spin size="large" /></div>
      </div>
    );
  }

  return (
    <div className="dashboard-root">

      {/* Welcome */}
      <div className="d-welcome">
        <h1 className="d-welcome-title">
          Welcome back{user?.firstName ? `, ${user.firstName}` : ''}
        </h1>
        <p className="d-welcome-sub">
          Here's an overview of your assessments and team activity.
        </p>
      </div>

      {/* Stats Row */}
      <div className="d-stats-row">
        <div className="d-stat-card">
          <div className="d-stat-icon blue"><FileTextOutlined /></div>
          <div className="d-stat-info">
            <div className="d-stat-label">Total Assessments</div>
            <div className="d-stat-value">{assessments.length}</div>
          </div>
        </div>

        <div className="d-stat-card">
          <div className="d-stat-icon amber"><ClockCircleOutlined /></div>
          <div className="d-stat-info">
            <div className="d-stat-label">Drafts</div>
            <div className="d-stat-value amber">{draftCount}</div>
          </div>
        </div>

        <div className="d-stat-card">
          <div className="d-stat-icon green"><CheckCircleOutlined /></div>
          <div className="d-stat-info">
            <div className="d-stat-label">Submitted</div>
            <div className="d-stat-value green">{submittedCount}</div>
          </div>
        </div>

        <div className="d-stat-card">
          <div className="d-stat-icon indigo"><CheckCircleOutlined /></div>
          <div className="d-stat-info">
            <div className="d-stat-label">{t.rating_adequate} Scores</div>
            <div className="d-stat-value">{adequateCount}</div>
          </div>
        </div>
      </div>

      {/* Entitlement (admin only) */}
      {isAdmin && entitlement && (
        <div className="d-entitlement">
          <div className="d-entitlement-header">
            <div className="d-entitlement-title-row">
              <div className="d-entitlement-icon"><TeamOutlined /></div>
              <div>
                <div className="d-entitlement-title">Team Seats</div>
                <div className="d-entitlement-sub">
                  {entitlement.currentUsers} of {entitlement.maxUsers} seats used
                </div>
              </div>
            </div>
            <button className="d-manage-btn" onClick={() => navigate('/users')}>
              <UserOutlined /> Manage Users
            </button>
          </div>
          <div className="d-entitlement-body">
            <div className="d-usage-section">
              <div className="d-usage-row">
                <span className="d-usage-label">Seats Used</span>
                <span className="d-usage-count">
                  {entitlement.currentUsers} / {entitlement.maxUsers}
                </span>
              </div>
              <div className="d-usage-track">
                <div
                  className={`d-usage-fill${usageWarn ? ' warn' : ''}`}
                  style={{ width: `${usagePct}%` }}
                />
              </div>
            </div>
            <div className="d-status-tags">
              <div className="d-status-tag">
                <span className={`d-status-dot ${entitlement.status === 'active' ? 'active' : 'inactive'}`} />
                Status: {entitlement.status === 'active' ? 'Active' : entitlement.status}
              </div>
              <div className="d-status-tag">
                <span className={`d-status-dot ${entitlement.canCreateUser ? 'active' : 'inactive'}`} />
                Can create users: {entitlement.canCreateUser ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main grid */}
      <div className="d-main-grid">

        {/* Recent Assessments */}
        <div className="d-card">
          <div className="d-card-header">
            <span className="d-card-title">Recent Assessments</span>
            <button className="d-card-link" onClick={() => navigate('/assessments')}>
              View All <RightOutlined style={{ fontSize: 10 }} />
            </button>
          </div>
          <div className="d-card-body">
            {recentAssessments.length === 0 ? (
              <div className="d-empty-state">
                <div className="d-empty-icon"><InboxOutlined /></div>
                <div>No assessments yet. Create your first one to get started.</div>
              </div>
            ) : (
              <ul className="d-assessment-list">
                {recentAssessments.map((assessment) => (
                  <li
                    key={assessment.assessmentId}
                    className="d-assessment-item"
                    onClick={() => navigate(`/assessments/${assessment.assessmentId}`)}
                  >
                    <div className={`d-assessment-type-badge ${assessment.assessmentType.toLowerCase()}`}>
                      {assessment.assessmentType}
                    </div>
                    <div className="d-assessment-info">
                      <div className="d-assessment-name">{assessment.assessmentName}</div>
                      <div className="d-assessment-meta">
                        Created {new Date(assessment.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="d-assessment-tags">
                      <span className={`d-tag ${assessment.status}`}>
                        {assessment.status.toUpperCase()}
                      </span>
                      {assessment.overallScore && (
                        <span className={`d-tag ${assessment.overallScore.toLowerCase()}`}>
                          {assessment.overallScore === 'Adequate' ? t.rating_adequate : t.rating_inadequate}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="d-sidebar">

          {/* Assessment Types */}
          <div className="d-card">
            <div className="d-card-header">
              <span className="d-card-title">Assessment Types</span>
            </div>
            <div className="d-card-body">
              <div className="d-type-item">
                <div className="d-type-name">
                  <span className="d-type-badge-inline cmda">CMDA</span>
                  {t.doc_cmda_title}
                </div>
                <div className="d-type-desc">
                  Perform CMDA to assess the current {t.section_cda_questions}.
                  Evaluates whether controls and monitors are appropriately designed.
                </div>
              </div>
              <div className="d-type-item">
                <div className="d-type-name">
                  <span className="d-type-badge-inline cma">CMA</span>
                  {t.doc_cma_title}
                </div>
                <div className="d-type-desc">
                  Perform CMA to evaluate existing/current control & monitoring for update/change.
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="d-card">
            <div className="d-card-header">
              <span className="d-card-title">Quick Actions</span>
            </div>
            <div className="d-card-body">
              <div className="d-actions-list">
                {isAssessor && (
                  <>
                    <button
                      className="d-action-btn primary"
                      onClick={() => navigate('/assessments/new?type=CMDA')}
                    >
                      <FileTextOutlined /> New CMDA Assessment
                    </button>
                    <button
                      className="d-action-btn secondary"
                      onClick={() => navigate('/assessments/new?type=CMA')}
                    >
                      <FileTextOutlined /> New CMA Assessment
                    </button>
                  </>
                )}
                <button
                  className="d-action-btn secondary"
                  onClick={() => navigate('/assessments')}
                >
                  <FileTextOutlined /> View All Assessments
                </button>
                {isAdmin && (
                  <button
                    className="d-action-btn secondary"
                    onClick={() => navigate('/users')}
                  >
                    <UserOutlined /> Manage Users
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
